import React, { useState, useRef, useEffect } from 'react';
import {
  Fab,
  Paper,
  Box,
  Typography,
  TextField,
  IconButton,
  Avatar,
  CircularProgress,
  Chip,
  Tooltip,
  Button
} from '@mui/material';
import {
  Chat as ChatIcon,
  Close as CloseIcon,
  Send as SendIcon,
  SmartToy as SmartToyIcon,
  Person as PersonIcon,
  Public as PublicIcon,
  Article as ArticleIcon,
  Analytics as AnalyticsIcon,
  Campaign as CampaignIcon
} from '@mui/icons-material';
import Logo from '../common/Logo';
import axios from 'axios';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export const FloatingChatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: 'Hi! I\'m your AI assistant for Prowess PR Platform. I can help you with press releases, distribution channels, analytics, and more. How can I assist you today?',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [distributionChannels, setDistributionChannels] = useState<any[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Fetch distribution channels for suggestions
  useEffect(() => {
    const fetchChannels = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await axios.get(`${API_BASE_URL}/api/pr/channels/available`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data.success && response.data.channels) {
            setDistributionChannels(response.data.channels);
          }
        }
      } catch (err) {
        console.error('Failed to fetch channels:', err);
      }
    };
    if (isOpen) {
      fetchChannels();
    }
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputMessage;
    setInputMessage('');
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      // Build context about website features
      let websiteContext = '';
      if (distributionChannels.length > 0) {
        const channelNames = distributionChannels.slice(0, 10).map((ch: any) => ch.name || ch.title).join(', ');
        websiteContext = `\n\nAvailable distribution channels on this platform: ${channelNames}.`;
      }

      const response = await fetch(`${API_BASE_URL}/api/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: currentInput + websiteContext,
          conversationHistory: messages.slice(-10).map(m => ({
            role: m.role,
            content: m.content
          }))
        })
      });

      const data = await response.json();

      if (response.ok) {
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: data.response || data.message,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        let errorContent = 'Sorry, I encountered an error. Please try again.';
        
        if (data.message) {
          errorContent = data.message;
        } else if (data.error) {
          errorContent = data.error;
        }
        
        if (data.errorCode === 'insufficient_quota' || 
            (data.error && data.error.includes('quota'))) {
          errorContent = '⚠️ AI service is temporarily unavailable. Please try again later.';
        }
        
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: errorContent,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Sorry, I couldn\'t connect to the server. Please check your connection and try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Website-related suggestions
  const websiteSuggestions = [
    {
      icon: <PublicIcon />,
      text: 'What distribution channels are available?',
      message: 'What distribution channels are available on this platform?'
    },
    {
      icon: <ArticleIcon />,
      text: 'How to create a press release?',
      message: 'How do I create a press release on this platform?'
    },
    {
      icon: <AnalyticsIcon />,
      text: 'How to track PR performance?',
      message: 'How can I track the performance of my press releases?'
    },
    {
      icon: <CampaignIcon />,
      text: 'What are the pricing plans?',
      message: 'What pricing plans and features are available on this platform?'
    }
  ];

  const handleSuggestionClick = (suggestion: typeof websiteSuggestions[0]) => {
    // Set the message and auto-send
    const messageToSend = suggestion.message;
    
    // Create user message
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: messageToSend,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    // Send the message
    (async () => {
      try {
        const token = localStorage.getItem('token');
        
        // Build context about website features
        let websiteContext = '';
        if (distributionChannels.length > 0) {
          const channelNames = distributionChannels.slice(0, 15).map((ch: any) => ch.name || ch.title).join(', ');
          websiteContext = `\n\nAvailable distribution channels on this platform: ${channelNames}.`;
        }

        const response = await fetch(`${API_BASE_URL}/api/ai/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            message: messageToSend + websiteContext,
            conversationHistory: messages.slice(-10).map(m => ({
              role: m.role,
              content: m.content
            }))
          })
        });

        const data = await response.json();

        if (response.ok) {
          const assistantMessage: Message = {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: data.response || data.message,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, assistantMessage]);
        } else {
          let errorContent = 'Sorry, I encountered an error. Please try again.';
          if (data.message) errorContent = data.message;
          else if (data.error) errorContent = data.error;
          
          const errorMessage: Message = {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: errorContent,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, errorMessage]);
        }
      } catch (error) {
        console.error('Chat error:', error);
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: 'Sorry, I couldn\'t connect to the server. Please check your connection and try again.',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
      } finally {
        setIsLoading(false);
      }
    })();
  };

  return (
    <>
      {/* Chat Window */}
      {isOpen && (
        <Paper
          elevation={12}
          style={{
            position: 'fixed',
            bottom: '100px',
            right: '24px',
            width: '380px',
            height: '600px',
            borderRadius: '16px',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1300,
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }}
        >
          {/* Header */}
          <Box
            style={{
              backgroundColor: '#0927eb',
              padding: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              color: 'white'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Avatar style={{ backgroundColor: 'white' }}>
                <SmartToyIcon style={{ color: '#0927eb' }} />
              </Avatar>
              <div>
                <Typography variant="subtitle1" style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Logo size="inline" animated={false} /> AI Assistant
                </Typography>
                <Typography variant="caption" style={{ opacity: 0.9 }}>
                  Your PR Platform Helper
                </Typography>
              </div>
            </div>
            <Tooltip title="Close">
              <IconButton
                size="small"
                onClick={() => setIsOpen(false)}
                style={{ color: 'white' }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Suggestions */}
          <Box style={{ padding: '12px', borderBottom: '1px solid #e0e0e0', backgroundColor: '#f9fafb' }}>
            <Typography variant="caption" style={{ color: '#666', marginBottom: '8px', display: 'block', fontWeight: 600 }}>
              Ask me about:
            </Typography>
            <Box style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {websiteSuggestions.map((suggestion, index) => (
                <Button
                  key={index}
                  size="small"
                  startIcon={suggestion.icon}
                  onClick={() => handleSuggestionClick(suggestion)}
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    color: '#0927eb',
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    fontSize: '0.875rem',
                    '&:hover': {
                      backgroundColor: '#f0f4ff',
                      borderColor: '#0927eb'
                    }
                  }}
                >
                  {suggestion.text}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Messages */}
          <Box
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px',
              backgroundColor: '#f5f5f5'
            }}
          >
            {messages.map((message) => (
              <Box
                key={message.id}
                style={{
                  display: 'flex',
                  justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                  marginBottom: '16px'
                }}
              >
                <Box
                  style={{
                    maxWidth: '80%',
                    display: 'flex',
                    gap: '8px',
                    flexDirection: message.role === 'user' ? 'row-reverse' : 'row'
                  }}
                >
                  <Avatar
                    style={{
                      width: 32,
                      height: 32,
                      backgroundColor: message.role === 'user' ? '#0927eb' : '#0927eb'
                    }}
                  >
                    {message.role === 'user' ? <PersonIcon fontSize="small" /> : <SmartToyIcon fontSize="small" />}
                  </Avatar>
                  <Paper
                    elevation={1}
                    style={{
                      padding: '12px',
                      backgroundColor: message.role === 'user' ? '#0927eb' : 'white',
                      color: message.role === 'user' ? 'white' : 'black',
                      borderRadius: '12px'
                    }}
                  >
                    <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                      {message.content}
                    </Typography>
                    <Typography
                      variant="caption"
                      style={{
                        opacity: 0.7,
                        marginTop: '4px',
                        display: 'block'
                      }}
                    >
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Paper>
                </Box>
              </Box>
            ))}
            {isLoading && (
              <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Avatar style={{ width: 32, height: 32, backgroundColor: '#0927eb' }}>
                  <SmartToyIcon fontSize="small" />
                </Avatar>
                <CircularProgress size={20} style={{ color: '#0927eb' }} />
                <Typography variant="body2" color="text.secondary">
                  AI is thinking...
                </Typography>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input */}
          <Box
            style={{
              padding: '16px',
              borderTop: '1px solid #e0e0e0',
              backgroundColor: 'white'
            }}
          >
            <div style={{ display: 'flex', gap: '8px' }}>
              <TextField
                fullWidth
                multiline
                maxRows={3}
                placeholder="Ask about PR, distribution channels, analytics..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                variant="outlined"
                size="small"
              />
              <IconButton
                color="primary"
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isLoading}
                style={{
                  backgroundColor: '#0927eb',
                  color: 'white'
                }}
              >
                <SendIcon />
              </IconButton>
            </div>
          </Box>
        </Paper>
      )}

      {/* Floating Action Button */}
      {!isOpen && (
        <Fab
          color="primary"
          onClick={() => setIsOpen(true)}
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            backgroundColor: '#0927eb',
            zIndex: 1300
          }}
        >
          <ChatIcon />
        </Fab>
      )}
    </>
  );
};

export default FloatingChatbot;
